import { ACCESS_GROUP_SCHEDULES, DEVICE_READERS } from '@/config/addAccessGroupFlow';
import type { AccessGroupDraft } from '@/types/accessGroupCreation';
import { normalizeMentionText } from '@/utils/atMention';

const WITH_READERS =
  /with\s+readers?\s+(.+?)(?=\s+on\b|\s+for\b|\s+using\b|$)/i;

const FOR_READERS =
  /\bfor\s+(.+?)(?:\s+on\b|\s+using\b|\.\s*$|$)/i;

const DESCRIPTOR_BEFORE_GROUP =
  /(?:create|add|new|setup)\s+(?:a\s+)?(.+?)\s+access\s+group\b/i;

const NAMED_GROUP =
  /(?:named|name(?:d)?|called)\s+(.+?)(?=\s+with\b|\s+on\b|\s+for\b|$)/i;

/** Specific phrases first — bare "reader 1" is ambiguous across doors. */
const READER_KEYWORD_RULES: { pattern: RegExp; value: string }[] = [
  { pattern: /lab\s*reader\s*1|\blab\b/i, value: 'reader-lab' },
  { pattern: /loading\s*bay/i, value: 'reader-loading' },
  { pattern: /server\s*room/i, value: 'reader-server' },
  { pattern: /main\s*entrance|entrance\s*reader\s*1/i, value: 'reader-main' },
  { pattern: /side\s*entrance|entrance\s*reader\s*2|\breader\s*2\b/i, value: 'reader-side' },
];

function locationPart(label: string): string {
  const parts = label.split(/\s*[—–-]\s*/);
  return (parts[parts.length - 1] ?? label).trim().toLowerCase();
}

function mapReaderLabelToValue(label: string): string | null {
  const normalized = label.replace(/^@/, '').trim();
  if (!normalized) return null;

  const lower = normalized.toLowerCase();
  for (const rule of READER_KEYWORD_RULES) {
    if (rule.pattern.test(lower)) return rule.value;
  }

  for (const reader of DEVICE_READERS) {
    const place = locationPart(reader.label);
    if (place && (lower === place || lower.includes(place))) {
      return reader.value;
    }
  }

  const byLabel = DEVICE_READERS.find(
    (r) => r.label.toLowerCase() === lower
  );
  if (byLabel) return byLabel.value;

  const atNum = lower.match(/^reader\s*(\d+)$/i);
  if (atNum) {
    const matches = DEVICE_READERS.filter((r) =>
      new RegExp(`Reader\\s+${atNum[1]}\\b`, 'i').test(r.label)
    );
    if (matches.length === 1) return matches[0].value;
  }

  return null;
}

function splitReaderTokens(segment: string): string[] {
  return segment
    .split(/\s*,\s*|\s+and\s+/i)
    .map((token) => token.trim())
    .filter(Boolean);
}

function mapAtReaderMention(num: string): string | null {
  const found = DEVICE_READERS.find((r) =>
    new RegExp(`Reader\\s+${num}\\b`, 'i').test(r.label)
  );
  return found?.value ?? null;
}

function extractReaderValues(text: string): string[] {
  const values: string[] = [];

  for (const match of text.matchAll(/@Reader\s*(\d+)/gi)) {
    const mapped = mapAtReaderMention(match[1]);
    if (mapped) values.push(mapped);
  }

  const withClause = text.match(WITH_READERS);
  if (withClause) {
    for (const token of splitReaderTokens(withClause[1])) {
      const mapped = mapReaderLabelToValue(token);
      if (mapped) values.push(mapped);
    }
  }

  const forClause = text.match(FOR_READERS);
  if (forClause) {
    for (const token of splitReaderTokens(forClause[1])) {
      const mapped = mapReaderLabelToValue(token);
      if (mapped) values.push(mapped);
    }
  }

  return [...new Set(values)];
}

function parseGroupName(text: string): string | undefined {
  const named = text.match(NAMED_GROUP);
  if (named) {
    return named[1].trim().replace(/\s+on\s+the\s+default\s+schedule.*$/i, '').trim();
  }

  const descriptor = text.match(DESCRIPTOR_BEFORE_GROUP);
  if (!descriptor) return undefined;

  const raw = descriptor[1].trim();
  if (!raw || /^new$/i.test(raw)) return undefined;

  const title = raw
    .replace(/-/g, ' ')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  return `${title} access group`;
}

function parseSchedule(text: string): string | undefined {
  if (/always\s*24\s*\/\s*7|24\s*\/\s*7|default\s+schedule/i.test(text)) {
    return ACCESS_GROUP_SCHEDULES[0];
  }
  for (const option of ACCESS_GROUP_SCHEDULES) {
    if (text.toLowerCase().includes(option.toLowerCase())) return option;
  }
  return undefined;
}

export function isAccessGroupCreationMessage(raw: string): boolean {
  const text = normalizeMentionText(raw.trim());
  return /(?:add|create|new|setup)/i.test(text) && /access\s*group/i.test(text);
}

export function parseAccessGroupPrefillFromMessage(raw: string): Partial<AccessGroupDraft> | null {
  const text = normalizeMentionText(raw.trim());
  if (!isAccessGroupCreationMessage(text)) return null;

  const prefill: Partial<AccessGroupDraft> = {};

  const name = parseGroupName(text);
  if (name) prefill.name = name;

  const readers = extractReaderValues(text);
  if (readers.length > 0) prefill.selectedReaders = readers;

  const schedule = parseSchedule(text);
  if (schedule) prefill.schedule = schedule;

  if (!prefill.name && !prefill.selectedReaders?.length && !prefill.schedule) return null;
  return prefill;
}

export function hasAccessGroupPrefill(raw: string): boolean {
  return parseAccessGroupPrefillFromMessage(raw) !== null;
}

/** @deprecated Use parseAccessGroupPrefillFromMessage */
export const parseAccessGroupSeedFromMessage = parseAccessGroupPrefillFromMessage;

/** @deprecated Use hasAccessGroupPrefill */
export const hasAccessGroupSeed = hasAccessGroupPrefill;
